import CheckIcon from '@mui/icons-material/Check';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import StarIcon from '@mui/icons-material/Star';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Box, Button, Card, CardActions, CardContent, Container, Divider, Grid, List, ListItem, ListItemIcon, ListItemText, Typography, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import React from 'react';
import { useInView } from 'react-intersection-observer';

export default function PricingSection() {
    const theme = useTheme();
    const [ref, inView] = useInView({
        triggerOnce: true,
        threshold: 0.1,
    });

    const pricingPlans = [
        {
            title: 'Mini',
            price: '2.499',
            period: 'aylık',
            description: 'Küçük mağazalar için ideal',
            features: [
                '300 Soru',
                '1 Mağaza bağlama',
                'Sorulara 1 dakika içerisinde cevap verir',
                'Mağaza özelleştirme',
                'Ai dil özelleştirme',
                'Talep açarak destek',
            ],
            buttonText: 'Hemen Başla',
            buttonVariant: 'contained',
            popular: false,
            isSpecial: true,
            whatsappLink: 'https://wa.me/905315404669?text=Merhaba%2C%20Trendasist%20hakkında%20bilgi%20almak%20ve%20kurulum%20hakkında%20yardım%20almak%20adına%20yazıyorum.'
        },
        {
            title: 'Başlangıç',
            price: '6.999',
            period: 'aylık',
            description: 'Küçük ve orta ölçekli mağazalar için ideal',
            features: [
                '1.500 Soru',
                '1 Mağaza bağlama',
                'Sorulara 1 dakika içerisinde cevap verir',
                'Mağaza özelleştirme',
                'Ai dil özelleştirme',
                'Talep açarak destek',
            ],
            buttonText: 'Hemen Başla',
            buttonVariant: 'outlined',
            popular: false,
            isSpecial: false
        },
        {
            title: 'Temel',
            price: '12.999',
            period: 'aylık',
            description: 'Orta ve gelişime açık ölçekli mağazalar için ideal',
            features: [
                '3.000 Soru',
                '1 Mağaza bağlama',
                'Sorulara 1 dakika içerisinde cevap verir',
                'Mağaza özelleştirme',
                'Ai dil özelleştirme',
                '7/24 Destek',
            ],
            buttonText: 'Hemen Başla',
            buttonVariant: 'outlined',
            popular: false,
            isSpecial: false
        },
        {
            title: 'Profesyonel',
            price: '27.999',
            period: 'aylık',
            description: 'Markalar için ideal',
            features: [
                '8.000 Soru',
                'Birden fazla mağaza ekleme',
                'Sorulara 1 dakika içerisinde cevap verir',
                'Mağaza özelleştirme',
                'Ai dil özelleştirme',
                'Öncelikli 7/24 destek',
                'Gelişmiş analitik',
            ],
            buttonText: 'Hemen Başla',
            buttonVariant: 'contained',
            popular: true,
            isSpecial: false
        },
        {
            title: 'Elit',
            price: '43.999',
            period: 'aylık',
            description: 'Büyük markalar için ideal',
            features: [
                '15.000 Soru',
                'Birden fazla mağaza ekleme',
                'Sorulara 1 dakika içerisinde cevap verir',
                'Mağaza özelleştirme',
                'Ai dil özelleştirme',
                'Vip 7/24 destek',
                'Gelişmiş analitik',
            ],
            buttonText: 'Hemen Başla',
            buttonVariant: 'outlined',
            popular: false,
            isSpecial: false
        },
        {
            title: 'Diamond',
            price: '69.999',
            period: 'aylık',
            description: 'Premium markalar için ideal',
            features: [
                '30.000 Soru',
                'Birden fazla mağaza ekleme',
                'Sorulara 1 dakika içerisinde cevap verir',
                'Mağaza özelleştirme',
                'Ai dil özelleştirme',
                'Vip 7/24 destek',
                'Gelişmiş analitik',
            ],
            buttonText: 'Hemen Başla',
            buttonVariant: 'outlined',
            popular: false,
            isSpecial: false
        }
    ];

    return (
        <Box
            id="pricing"
            sx={{
                py: 10,
                background: 'white',
                borderTop: `1px solid ${theme.palette.grey[200]}`,
            }}
        >
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 6 }}>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        viewport={{ once: true }}
                    >
                        <Typography
                            variant="h2"
                            component="h2"
                            sx={{
                                fontSize: { xs: '2rem', md: '2.5rem' },
                                fontWeight: 700,
                                mb: 2,
                                color: theme.palette.secondary.main,
                            }}
                        >
                            Fiyatlandırma Planları
                        </Typography>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                fontSize: { xs: '1rem', md: '1.1rem' },
                                color: theme.palette.text.secondary,
                                maxWidth: '800px',
                                mx: 'auto',
                                mb: 4,
                            }}
                        >
                            İhtiyaçlarınıza uygun fiyatlandırma planlarımızla TrendAsist'i hemen kullanmaya başlayın.
                        </Typography>
                    </motion.div>
                </Box>

                <Grid container spacing={4} ref={ref}>
                    {pricingPlans.slice(0, 3).map((plan, index) => (
                        <Grid
                            item
                            xs={12}
                            md={4}
                            key={index}
                        >
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                whileHover={plan.isSpecial ? { scale: 1.02, rotate: [0, -1, 1, -1, 0] } : { scale: 1.02 }}
                            >
                                <Card
                                    elevation={plan.popular || plan.isSpecial ? 8 : 2}
                                    sx={{
                                        borderRadius: '16px',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        overflow: 'visible',
                                        border: plan.popular ? `2px solid ${theme.palette.primary.main}` :
                                            plan.isSpecial ? `2px solid ${theme.palette.warning.main}` : 'none',
                                        transition: 'transform 0.3s, box-shadow 0.3s',
                                        background: plan.isSpecial ?
                                            `linear-gradient(135deg, ${theme.palette.warning.light}22 0%, white 50%, ${theme.palette.warning.light}22 100%)` :
                                            'white',
                                        '&:hover': {
                                            transform: 'translateY(-10px)',
                                            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                                        }
                                    }}
                                >
                                    {plan.popular && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: -15,
                                                right: 20,
                                                backgroundColor: theme.palette.primary.main,
                                                color: 'white',
                                                py: 0.5,
                                                px: 2,
                                                borderRadius: '20px',
                                                fontWeight: 'bold',
                                                fontSize: '0.8rem',
                                                zIndex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <StarIcon fontSize="small" />
                                            En Popüler
                                        </Box>
                                    )}
                                    {plan.isSpecial && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: -15,
                                                right: 20,
                                                backgroundColor: theme.palette.warning.main,
                                                color: 'white',
                                                py: 0.5,
                                                px: 2,
                                                borderRadius: '20px',
                                                fontWeight: 'bold',
                                                fontSize: '0.8rem',
                                                zIndex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <LocalOfferIcon fontSize="small" />
                                            Sınırlı Süre
                                        </Box>
                                    )}
                                    <CardContent sx={{ flexGrow: 1, p: 4 }}>
                                        <Typography variant="h5" component="h3" sx={{
                                            fontWeight: 700,
                                            mb: 1,
                                            color: plan.isSpecial ? theme.palette.warning.main : 'inherit'
                                        }}>
                                            {plan.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            {plan.description}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
                                            <Typography variant="h3" component="span" sx={{
                                                fontWeight: 700,
                                                color: plan.isSpecial ? theme.palette.warning.main : 'inherit',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}>
                                                {plan.price}
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: '1.5rem',
                                                        ml: 1,
                                                        color: 'text.secondary',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {plan.price === '0' ? 'TL' : `₺/${plan.period}`}
                                                </Typography>
                                            </Typography>
                                            {plan.price !== '0' && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        ml: 1,
                                                        color: 'text.secondary',
                                                        alignSelf: 'flex-end',
                                                        mb: 1
                                                    }}
                                                >
                                                    (KDV Dahil)
                                                </Typography>
                                            )}
                                        </Box>
                                        <Divider sx={{ my: 2 }} />
                                        <List disablePadding>
                                            {plan.features.map((feature, idx) => (
                                                <ListItem key={idx} disablePadding sx={{ py: 1 }}>
                                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                                        <CheckIcon sx={{
                                                            color: plan.isSpecial ?
                                                                theme.palette.warning.main :
                                                                theme.palette.primary.main
                                                        }} />
                                                    </ListItemIcon>
                                                    <ListItemText
                                                        primary={feature}
                                                        sx={{
                                                            '& .MuiTypography-root': {
                                                                fontWeight: plan.isSpecial ? 500 : 400
                                                            }
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                    <CardActions sx={{ p: 4, pt: 0 }}>
                                        <Button
                                            fullWidth
                                            variant={plan.buttonVariant}
                                            color={plan.isSpecial ? "warning" : "primary"}
                                            size="large"
                                            component="a"
                                            href={plan.whatsappLink || "/#/register"}
                                            target={plan.whatsappLink ? "_blank" : "_self"}
                                            sx={{
                                                py: 1.5,
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                textDecoration: 'none',
                                            }}
                                        >
                                            {plan.buttonText}
                                        </Button>
                                    </CardActions>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>

                <Grid container spacing={4} sx={{ mt: 4 }}>
                    {pricingPlans.slice(3).map((plan, index) => (
                        <Grid
                            item
                            xs={12}
                            md={4}
                            key={index + 3}
                        >
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                                transition={{ duration: 0.5, delay: (index + 3) * 0.1 }}
                                whileHover={{ scale: 1.02 }}
                            >
                                <Card
                                    elevation={plan.popular ? 8 : 2}
                                    sx={{
                                        borderRadius: '16px',
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        position: 'relative',
                                        overflow: 'visible',
                                        border: plan.popular ? `2px solid ${theme.palette.primary.main}` : 'none',
                                        transition: 'transform 0.3s, box-shadow 0.3s',
                                        '&:hover': {
                                            transform: 'translateY(-10px)',
                                            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                                        }
                                    }}
                                >
                                    {plan.popular && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                top: -15,
                                                right: 20,
                                                backgroundColor: theme.palette.primary.main,
                                                color: 'white',
                                                py: 0.5,
                                                px: 2,
                                                borderRadius: '20px',
                                                fontWeight: 'bold',
                                                fontSize: '0.8rem',
                                                zIndex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                            }}
                                        >
                                            <StarIcon fontSize="small" />
                                            En Popüler
                                        </Box>
                                    )}
                                    <CardContent sx={{ flexGrow: 1, p: 4 }}>
                                        <Typography variant="h5" component="h3" sx={{ fontWeight: 700, mb: 1 }}>
                                            {plan.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                            {plan.description}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
                                            <Typography variant="h3" component="span" sx={{
                                                fontWeight: 700,
                                                color: plan.isSpecial ? theme.palette.warning.main : 'inherit',
                                                display: 'flex',
                                                alignItems: 'center'
                                            }}>
                                                {plan.price}
                                                <Typography
                                                    component="span"
                                                    sx={{
                                                        fontSize: '1.5rem',
                                                        ml: 1,
                                                        color: 'text.secondary',
                                                        fontWeight: 500
                                                    }}
                                                >
                                                    {plan.price === '0' ? 'TL' : `₺/${plan.period}`}
                                                </Typography>
                                            </Typography>
                                            {plan.price !== '0' && (
                                                <Typography
                                                    variant="caption"
                                                    sx={{
                                                        ml: 1,
                                                        color: 'text.secondary',
                                                        alignSelf: 'flex-end',
                                                        mb: 1
                                                    }}
                                                >
                                                    (KDV Dahil)
                                                </Typography>
                                            )}
                                        </Box>
                                        <Divider sx={{ my: 2 }} />
                                        <List disablePadding>
                                            {plan.features.map((feature, idx) => (
                                                <ListItem key={idx} disablePadding sx={{ py: 1 }}>
                                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                                        <CheckIcon sx={{ color: theme.palette.primary.main }} />
                                                    </ListItemIcon>
                                                    <ListItemText primary={feature} />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </CardContent>
                                    <CardActions sx={{ p: 4, pt: 0 }}>
                                        <Button
                                            fullWidth
                                            variant={plan.buttonVariant}
                                            color="primary"
                                            size="large"
                                            component="a"
                                            href={plan.whatsappLink || "/#/register"}
                                            target={plan.whatsappLink ? "_blank" : "_self"}
                                            sx={{
                                                py: 1.5,
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                textDecoration: 'none',
                                            }}
                                        >
                                            {plan.buttonText}
                                        </Button>
                                    </CardActions>
                                </Card>
                            </motion.div>
                        </Grid>
                    ))}
                </Grid>

                <Box
                    sx={{
                        mt: 8,
                        p: 4,
                        borderRadius: '16px',
                        backgroundColor: theme.palette.primary.main,
                        color: 'white',
                        textAlign: 'center',
                    }}
                >
                    <Typography variant="h4" component="h3" sx={{ mb: 2, fontWeight: 700 }}>
                        Kurumsal Çözümler
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 4 }}>
                        Sınırsız soru-cevap, özel API erişimi ve size özel çözümler için kurumsal teklifimizi inceleyin.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        component="a"
                        href="https://api.whatsapp.com/send/?phone=905315404669&text=Merhaba%2C+Trendasist+hakkında+bilgi+almak+ve+kurulum+hakkında+yardım+almak+adına+yazıyorum.&type=phone_number&app_absent=0"
                        target="_blank"
                        sx={{
                            backgroundColor: 'white',
                            color: theme.palette.primary.main,
                            '&:hover': {
                                backgroundColor: theme.palette.grey[100],
                            },
                            py: 1.5,
                            px: 4,
                            borderRadius: '8px',
                            fontWeight: 600,
                        }}
                        startIcon={<WhatsAppIcon />}
                    >
                        Detaylı Bilgi Al
                    </Button>
                </Box>
            </Container>
        </Box>
    );
} 